import { ArrowLeftRight, BookOpenText, Layers, Package, Swords, Trophy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { GameFullscreenButton } from "../components/GameFullscreenButton";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Footer } from "../components/Footer";
import { SectionHeading } from "../components/SectionHeading";
import { officialTcgCards } from "../data/site";
import { TcgBattle } from "../components/TcgBattle";
import { TcgDeckBuilder } from "../components/TcgDeckBuilder";
import { TcgLeaderboard } from "../components/TcgLeaderboard";
import { TcgPacks } from "../components/TcgPacks";
import { TcgRulesMatch } from "../components/TcgRulesMatch";
import { TcgTrades } from "../components/TcgTrades";
import { fetchMe, getCachedUser } from "../utils/auth";
import {
  cardById,
  DECK_SIZE,
  fetchCloudCollection,
  fetchCloudDeck,
  fetchTradeNotifications,
  getLocalCollection,
  getLocalDeck,
  isDeckLegal,
  isSlottable,
  LOANER_IDS,
  saveCloudDeck,
  saveLocalDeck,
  STARTER_DECK,
} from "../utils/tcg";

export default function TcgArena() {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(getCachedUser());
  const [deck, setDeck] = useState(() => getLocalDeck() || STARTER_DECK);
  const initialMode = searchParams.get("mode");
  const [mode, setMode] = useState(["builder", "battle", "rules", "packs", "rankings", "trades"].includes(initialMode) ? initialMode : "builder");
  const [saving, setSaving] = useState(false);
  const [collection, setCollection] = useState(getLocalCollection);

  const deckLegal = isDeckLegal(deck, collection);

  /* session boot: adopt the account deck + crate collection when a vault exists */
  useEffect(() => {
    let alive = true;
    fetchMe().then(async (me) => {
      if (!alive) return;
      setUser(me);
      if (!me) return;
      try {
        const cloud = await fetchCloudDeck();
        if (alive && Array.isArray(cloud.cards) && cloud.cards.length === DECK_SIZE && cloud.cards.every((id) => cardById(id))) {
          setDeck(cloud.cards);
          saveLocalDeck(cloud.cards);
        }
      } catch {
        /* vault unreadable — device deck rules */
      }
    });
    /* crate collection lives on the tag, not the account — guests pull too */
    fetchCloudCollection().then((cloud) => {
      if (alive && cloud?.cards) setCollection(cloud.cards);
    });
    /* flag accepted trade-board offers the moment a runner returns */
    fetchTradeNotifications().then((notes) => {
      if (!alive) return;
      notes.forEach((note) => {
        const gave = cardById(note.give)?.name.split(",")[0] || note.give;
        const got = cardById(note.want)?.name.split(",")[0] || note.want;
        toast.success(`TRADE COMPLETE — ${note.accepted_by} TOOK YOUR ${gave} FOR THEIR ${got}.`);
      });
    });
    const onAuth = (event) => setUser(event.detail?.user ?? null);
    window.addEventListener("dolo-auth", onAuth);
    return () => {
      alive = false;
      window.removeEventListener("dolo-auth", onAuth);
    };
  }, []);

  const toggleCard = (id) => {
    setDeck((current) => {
      if (current.includes(id)) return current.filter((c) => c !== id);
      if (!isSlottable(id, collection)) {
        toast.error("UNIT LOCKED — PULL IT FROM A CRATE FIRST.");
        return current;
      }
      if (current.length >= DECK_SIZE) {
        toast.error("DECK FULL — PULL A UNIT BEFORE SLOTTING ANOTHER.");
        return current;
      }
      return [...current, id];
    });
  };

  const saveDeck = async () => {
    if (deck.length !== DECK_SIZE) return;
    if (!deckLegal) {
      toast.error("DECK HOLDS UNPULLED UNITS — SWAP THEM OR CRACK CRATES.");
      return;
    }
    setSaving(true);
    if (user) {
      try {
        await saveCloudDeck(deck);
        saveLocalDeck(deck);
        toast.success("DECK LOCKED TO YOUR ACCOUNT VAULT.");
      } catch (error) {
        toast.error(error.response?.data?.detail || "VAULT UNREACHABLE — RETRY.");
      }
    } else {
      saveLocalDeck(deck);
      toast.success("DECK SAVED TO THIS DEVICE.");
    }
    setSaving(false);
  };

  return (
    <main className="tcg-arena-page" data-testid="tcg-arena-page">
      <div className="tcg-arena-inner">
        <GameFullscreenButton testid="tcg-arena-fullscreen" />
        <SectionHeading
          eyebrow="WASTELAND TCG // ARENA"
          title="Build the deck. Break the Net."
          text={`Slot six units into your battle deck — the house loaners (including the Chaos Engine riot terrain) are always yours, the other ${officialTcgCards.length - LOANER_IDS.length} must be pulled from supply crates. Take your deck into a six-round lane war against the Black-Ops Net war council, bank PTS for every round taken, melt duplicate pulls back into PTS, and climb the war council rankings.`}
        />

        <div className="tcg-arena-tabs" role="tablist">
          <button
            type="button"
            className={`tcg-arena-tab${mode === "builder" ? " is-active" : ""}`}
            onClick={() => setMode("builder")}
            data-testid="arena-tab-builder"
            role="tab"
            aria-selected={mode === "builder"}
          >
            <Layers size={14} /> DECK BUILDER
          </button>
          <button
            type="button"
            className={`tcg-arena-tab${mode === "battle" ? " is-active" : ""}`}
            onClick={() => setMode("battle")}
            disabled={!deckLegal}
            data-testid="arena-tab-battle"
            role="tab"
            aria-selected={mode === "battle"}
          >
            <Swords size={14} /> QUICK SKIRMISH
          </button>
          <button
            type="button"
            className={`tcg-arena-tab${mode === "rules" ? " is-active" : ""}`}
            onClick={() => setMode("rules")}
            disabled={!deckLegal}
            data-testid="arena-tab-rules"
            role="tab"
            aria-selected={mode === "rules"}
          >
            <BookOpenText size={14} /> RULES MATCH
          </button>
          <button
            type="button"
            className={`tcg-arena-tab${mode === "packs" ? " is-active" : ""}`}
            onClick={() => setMode("packs")}
            data-testid="arena-tab-packs"
            role="tab"
            aria-selected={mode === "packs"}
          >
            <Package size={14} /> PACK CRATE
          </button>
          <button
            type="button"
            className={`tcg-arena-tab${mode === "rankings" ? " is-active" : ""}`}
            onClick={() => setMode("rankings")}
            data-testid="arena-tab-rankings"
            role="tab"
            aria-selected={mode === "rankings"}
          >
            <Trophy size={14} /> RANKINGS
          </button>
          <button
            type="button"
            className={`tcg-arena-tab${mode === "trades" ? " is-active" : ""}`}
            onClick={() => setMode("trades")}
            data-testid="arena-tab-trades"
            role="tab"
            aria-selected={mode === "trades"}
          >
            <ArrowLeftRight size={14} /> TRADE BOARD
          </button>
        </div>

        {mode === "builder" && (
          <TcgDeckBuilder deck={deck} collection={collection} onToggle={toggleCard} onSave={saveDeck} saving={saving} user={user} />
        )}
        {mode === "battle" && <TcgBattle deckIds={deck} user={user} onRebuild={() => setMode("builder")} />}
        {mode === "rules" && <TcgRulesMatch deckIds={deck} user={user} onRebuild={() => setMode("builder")} />}
        {mode === "packs" && <TcgPacks />}
        {mode === "rankings" && <TcgLeaderboard />}
        {mode === "trades" && <TcgTrades />}
      </div>
      <Footer />
    </main>
  );
}
